import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import usePwaInstall from '../hooks/usePwaInstall';
import { isInAppBrowser, isAndroid, isIOS } from '../utils/browser';
import { useOpenInExternalBrowser } from '../hooks/useOpenInExternalBrowser';
import InAppBrowserNotice from './InAppBrowserNotice';

export default function PwaInstallButton({ className = '', guideActive = false, onInAppNoticeChange, onRequestCloseGuide }) {
  const { t } = useTranslation();
  const { installState, promptInstall } = usePwaInstall();
  const [hintType, setHintType] = useState(null);
  const [inappNotice, setInappNotice] = useState(null);
  const { openInBrowser, isOpening } = useOpenInExternalBrowser();
  const containerRef = useRef(null);

  useEffect(() => {
    if (typeof onInAppNoticeChange === 'function') {
      onInAppNoticeChange(Boolean(inappNotice));
    }
  }, [inappNotice, onInAppNoticeChange]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setHintType(null);
        setInappNotice(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  if (installState === 'installed') {
    return null;
  }

  const handleClick = async (e) => {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
    }

    // (1) installState === 'ready' -> 실제 네이티브 설치창 호출 후 종료
    if (installState === 'ready') {
      setHintType(null);
      setInappNotice(null);
      await promptInstall();
      return;
    }

    // (2) isInAppBrowser() === true -> 기존 인앱 분기 유지
    if (isInAppBrowser()) {
      setHintType(null);
      if (isAndroid()) {
        const targetUrl = new URL(window.location.href);
        if (targetUrl.searchParams.get('install') !== '1') {
          targetUrl.searchParams.set('install', '1');
        }
        openInBrowser(
          () => setInappNotice('android_failed'),
          { url: targetUrl.href, autoOAuth: false }
        );
        return;
      }

      if (isIOS()) {
        setInappNotice((prev) => (prev === 'ios' ? null : 'ios'));
        return;
      }

      setInappNotice((prev) => (prev === 'generic' ? null : 'generic'));
      return;
    }

    // (3) isInAppBrowser() === false -> 일반 브라우저에서 ready가 아닐 때 주소창 설치 아이콘 안내
    setInappNotice(null);
    if (guideActive && typeof onRequestCloseGuide === 'function') {
      onRequestCloseGuide();
    }
    if (isIOS()) {
      setHintType((prev) => (prev === 'ios' ? null : 'ios'));
    } else {
      setHintType((prev) => (prev === 'address_bar' ? null : 'address_bar'));
    }
  };

  const buttonClass = guideActive
    ? "inline-flex items-center gap-1 bg-surface-bright text-primary font-label-md text-[12px] px-3 py-1.5 rounded-full shadow-md border border-primary/30 hover:bg-surface active:opacity-70 transition-all cursor-pointer relative z-50"
    : "inline-flex items-center gap-1 border-b border-primary/70 text-primary font-label-md text-[12px] pb-[1px] hover:text-primary hover:border-primary active:opacity-70 transition-colors cursor-pointer bg-transparent border-t-0 border-l-0 border-r-0 p-0 relative z-50";

  return (
    <div ref={containerRef} className={`z-50 inline-flex ${className}`}>
      <button
        type="button"
        onClick={handleClick}
        disabled={isOpening}
        className={buttonClass}
      >
        <span
          className="material-symbols-outlined text-[14px] leading-none align-middle"
          style={{ fontVariationSettings: "'wght' 300" }}
        >
          download
        </span>
        <span>{t('common.install_app_short')}</span>
      </button>

      {inappNotice && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 z-50 w-[280px] sm:w-[320px]">
          <InAppBrowserNotice
            open={Boolean(inappNotice)}
            variant={inappNotice}
            onClose={() => setInappNotice(null)}
          />
        </div>
      )}

      {hintType && !guideActive && !isInAppBrowser() && (
        <div className="absolute top-full right-0 mt-1.5 z-30 w-[240px] p-2.5 bg-surface-container-low/95 backdrop-blur-sm border border-outline-variant/40 rounded-xl pointer-events-auto shadow-md">
          <p className="text-[11px] text-on-surface-variant leading-relaxed break-keep">
            {hintType === 'ios' && t('profile.install_ios_hint')}
            {hintType === 'address_bar' && t('common.install_address_bar_hint')}
          </p>
        </div>
      )}
    </div>
  );
}
